import { useState, useEffect } from 'react';
import { Upload, Search, TrendingUp, AlertTriangle, CheckCircle2, XCircle, Loader2, BarChart3, ArrowRight, Minus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CampaignLaunchPanel from './CampaignLaunchPanel';

const API = '/api';

function copyText(t) { navigator.clipboard.writeText(t).then(() => toast.success('Copied')).catch(() => {}); }

const CLASSIFICATION = {
  strong: { cls: 'bg-green-100 text-green-700', label: 'Strong' },
  weak: { cls: 'bg-amber-100 text-amber-700', label: 'Weak' },
  irrelevant: { cls: 'bg-red-100 text-red-700', label: 'Irrelevant' },
  underperforming: { cls: 'bg-amber-100 text-amber-700', label: 'Underperforming' },
};

const PRIORITY = {
  high: { cls: 'bg-red-100 text-red-700', label: 'High' },
  medium: { cls: 'bg-amber-100 text-amber-700', label: 'Medium' },
  low: { cls: 'bg-muted text-muted-foreground', label: 'Low' },
};

function ImportForm({ token, onImported }) {
  const [campaignName, setCampaignName] = useState('hoge_energierekening');
  const [dateRange, setDateRange] = useState('');
  const [impressions, setImpressions] = useState('');
  const [clicks, setClicks] = useState('');
  const [ctr, setCtr] = useState('');
  const [cpc, setCpc] = useState('');
  const [cost, setCost] = useState('');
  const [searchTermsRaw, setSearchTermsRaw] = useState('');
  const [assetsRaw, setAssetsRaw] = useState('');
  const [importing, setImporting] = useState(false);

  const parseSearchTerms = (raw) => {
    if (!raw.trim()) return [];
    return raw.trim().split('\n').map(line => {
      const parts = line.split('\t').length > 1 ? line.split('\t') : line.split(',');
      return {
        term: (parts[0] || '').trim(),
        impressions: parseInt(parts[1]) || 0,
        clicks: parseInt(parts[2]) || 0,
        ctr: parseFloat(parts[3]) || 0,
        cost: parseFloat(parts[4]) || 0,
      };
    }).filter(t => t.term);
  };

  const parseAssets = (raw) => {
    if (!raw.trim()) return [];
    return raw.trim().split('\n').map(line => {
      const parts = line.split('\t').length > 1 ? line.split('\t') : line.split(',');
      return {
        type: (parts[0] || 'headline').trim(),
        text: (parts[1] || '').trim(),
        impressions: parseInt(parts[2]) || 0,
        clicks: parseInt(parts[3]) || 0,
        ctr: parseFloat(parts[4]) || 0,
      };
    }).filter(a => a.text);
  };

  const handleImport = async () => {
    if (!campaignName.trim()) { toast.error('Campaign name required'); return; }
    setImporting(true);
    try {
      const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };
      const { data } = await axios.post(`${API}/admin/ads/import`, {
        campaign_name: campaignName.trim(),
        date_range: dateRange,
        performance: {
          impressions: parseInt(impressions) || 0,
          clicks: parseInt(clicks) || 0,
          ctr: parseFloat(ctr) || 0,
          cpc: parseFloat(cpc) || 0,
          cost: parseFloat(cost) || 0,
        },
        search_terms: parseSearchTerms(searchTermsRaw),
        ad_assets: parseAssets(assetsRaw),
      }, opts);
      toast.success('Data imported');
      onImported(data.import_id);
    } catch { toast.error('Import failed'); }
    setImporting(false);
  };

  return (
    <div className="bg-white rounded-xl border border-border p-5 shadow-sm space-y-4" data-testid="ads-import-form">
      <div className="flex items-center gap-2 mb-2">
        <Upload className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold">Import Google Ads Data</h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 md:col-span-1">
          <label className="text-[10px] text-muted-foreground uppercase">Campaign Name (must match utm_campaign)</label>
          <input value={campaignName} onChange={e => setCampaignName(e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 mt-0.5" data-testid="ads-campaign-name" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase">Date Range</label>
          <input value={dateRange} onChange={e => setDateRange(e.target.value)} placeholder="e.g. Apr 14-20"
            className="w-full text-sm border border-border rounded-lg px-3 py-2 mt-0.5" />
        </div>
      </div>

      <div>
        <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Campaign Performance</label>
        <div className="grid grid-cols-5 gap-2">
          {[
            ['Impressions', impressions, setImpressions],
            ['Clicks', clicks, setClicks],
            ['CTR %', ctr, setCtr],
            ['CPC €', cpc, setCpc],
            ['Cost €', cost, setCost],
          ].map(([label, val, setter]) => (
            <div key={label}>
              <label className="text-[9px] text-muted-foreground">{label}</label>
              <input type="number" value={val} onChange={e => setter(e.target.value)} step="0.01"
                className="w-full text-xs border border-border rounded px-2 py-1.5 mt-0.5" />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Search Terms (paste from Google Ads: term, impressions, clicks, ctr%, cost)</label>
        <textarea value={searchTermsRaw} onChange={e => setSearchTermsRaw(e.target.value)} rows={4}
          placeholder="energiescan huis&#9;450&#9;23&#9;5.1&#9;8.50&#10;isolatie kosten&#9;320&#9;15&#9;4.7&#9;6.20"
          className="w-full text-xs border border-border rounded px-2 py-1.5 font-mono" data-testid="ads-search-terms" />
      </div>

      <div>
        <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Ad Assets (paste: type, text, impressions, clicks, ctr%)</label>
        <textarea value={assetsRaw} onChange={e => setAssetsRaw(e.target.value)} rows={3}
          placeholder="headline&#9;Gratis Energiescan&#9;1200&#9;45&#9;3.7&#10;description&#9;Ontdek hoeveel u kunt besparen&#9;1200&#9;38&#9;3.2"
          className="w-full text-xs border border-border rounded px-2 py-1.5 font-mono" data-testid="ads-assets" />
      </div>

      <button onClick={handleImport} disabled={importing}
        className="w-full py-2.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="ads-import-btn">
        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        Import & Save
      </button>
    </div>
  );
}

function AnalysisView({ analysis }) {
  if (!analysis) return null;

  return (
    <div className="space-y-4" data-testid="ads-analysis">
      {/* Summary */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <p className="text-sm">{analysis.summary}</p>
        <div className="flex items-center gap-3 mt-2 text-xs">
          <span className="text-muted-foreground">Traffic Quality:</span>
          <span className={`font-medium ${analysis.traffic_quality_score >= 7 ? 'text-green-700' : analysis.traffic_quality_score >= 4 ? 'text-amber-700' : 'text-red-700'}`}>
            {analysis.traffic_quality_score}/10
          </span>
          {analysis.traffic_quality_notes && <span className="text-muted-foreground">— {analysis.traffic_quality_notes}</span>}
        </div>
      </div>

      {/* Recommendations */}
      {analysis.recommendations?.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Recommendations</h4>
          <div className="space-y-2">
            {analysis.recommendations.map((r, i) => {
              const p = PRIORITY[r.priority] || PRIORITY.medium;
              return (
                <div key={i} className="flex items-start gap-2 text-xs border-b border-border/50 pb-2 last:border-0">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0 mt-0.5 ${p.cls}`}>{p.label}</span>
                  <div>
                    <p className="font-medium">{r.action}</p>
                    <p className="text-muted-foreground">{r.reason}</p>
                    {r.expected_impact && <p className="text-primary text-[10px] mt-0.5">Impact: {r.expected_impact}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search Term Analysis */}
      {analysis.search_term_analysis?.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Search Terms</h4>
          <div className="space-y-1.5">
            {analysis.search_term_analysis.map((t, i) => {
              const c = CLASSIFICATION[t.classification] || CLASSIFICATION.weak;
              return (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0 ${c.cls}`}>{c.label}</span>
                  <span className="font-medium flex-1 min-w-0 truncate">"{t.term}"</span>
                  <span className="text-muted-foreground flex-shrink-0">{t.action}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Negative Keywords + Additions */}
      <div className="grid md:grid-cols-2 gap-4">
        {analysis.negative_keywords?.length > 0 && (
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-red-700">Negative Keywords</h4>
              <button onClick={() => copyText(analysis.negative_keywords.join('\n'))} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {analysis.negative_keywords.map((k, i) => (
                <span key={i} className="text-[10px] px-2 py-1 rounded bg-red-50 text-red-700">{k}</span>
              ))}
            </div>
          </div>
        )}
        {analysis.keyword_additions?.length > 0 && (
          <div className="bg-white rounded-lg border border-green-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-green-700">Add Keywords</h4>
              <button onClick={() => copyText(analysis.keyword_additions.join('\n'))} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {analysis.keyword_additions.map((k, i) => (
                <span key={i} className="text-[10px] px-2 py-1 rounded bg-green-50 text-green-700">{k}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Asset Analysis */}
      {analysis.asset_analysis?.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ad Asset Analysis</h4>
          <div className="space-y-2">
            {analysis.asset_analysis.map((a, i) => {
              const c = CLASSIFICATION[a.performance] || CLASSIFICATION.weak;
              return (
                <div key={i} className="text-xs border-b border-border/50 pb-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-muted-foreground uppercase">{a.type}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${c.cls}`}>{c.label}</span>
                  </div>
                  <p className="font-medium mt-0.5">"{a.text}"</p>
                  {a.suggestion && (
                    <div className="flex items-center gap-1.5 mt-1 text-green-700">
                      <ArrowRight className="w-3 h-3" />
                      <span className="font-medium">"{a.suggestion}"</span>
                      <button onClick={() => copyText(a.suggestion)} className="ml-1"><Copy className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Funnel Attribution */}
      {analysis.funnel_attribution && (
        <div className="bg-white rounded-lg border border-border p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Funnel Attribution</h4>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center">
            {[
              ['Scans', analysis.funnel_attribution.scan_starts],
              ['Completed', analysis.funnel_attribution.completions],
              ['Previews', analysis.funnel_attribution.previews],
              ['Pay Clicks', analysis.funnel_attribution.payment_clicks],
              ['Payments', analysis.funnel_attribution.payments],
              ['Cost/Scan', `€${analysis.funnel_attribution.cost_per_scan}`],
            ].map(([label, val]) => (
              <div key={label}>
                <p className="text-lg font-medium">{val}</p>
                <p className="text-[10px] text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AttributionTable({ data }) {
  if (!data || data.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">No campaign attribution data yet. Import Google Ads data and ensure your campaigns use matching utm_campaign values.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="attribution-table">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="pb-2 pr-3">Campaign</th>
            <th className="pb-2 pr-3 text-right">Spend</th>
            <th className="pb-2 pr-3 text-right">Clicks</th>
            <th className="pb-2 pr-3 text-right">Scans</th>
            <th className="pb-2 pr-3 text-right">Complete</th>
            <th className="pb-2 pr-3 text-right">Previews</th>
            <th className="pb-2 pr-3 text-right">Payments</th>
            <th className="pb-2 text-right">CPA</th>
          </tr>
        </thead>
        <tbody>
          {data.map(r => (
            <tr key={r.campaign} className="border-b border-border/50">
              <td className="py-2 pr-3 font-medium">{r.campaign}</td>
              <td className="py-2 pr-3 text-right">€{r.spend}</td>
              <td className="py-2 pr-3 text-right">{r.ad_clicks}</td>
              <td className="py-2 pr-3 text-right">{r.scan_starts}</td>
              <td className="py-2 pr-3 text-right">{r.scan_completions}</td>
              <td className="py-2 pr-3 text-right">{r.preview_views}</td>
              <td className="py-2 pr-3 text-right font-medium">{r.unlocked_reports}</td>
              <td className="py-2 text-right">{r.cost_per_payment > 0 ? `€${r.cost_per_payment}` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CHECK_ICON = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle };
const CHECK_STYLE = {
  pass: { cls: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  warn: { cls: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  fail: { cls: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

function CampaignDiagnosisPanel({ campaigns, token }) {
  const [selected, setSelected] = useState('');
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(false);
  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const campaignNames = [...new Set(campaigns.map(c => c.campaign_name))];

  const runDiagnosis = async (name) => {
    if (!name) return;
    setLoading(true);
    setDiagnosis(null);
    try {
      const { data } = await axios.get(`${API}/admin/ads/diagnose/${encodeURIComponent(name)}`, opts);
      setDiagnosis(data.diagnosis);
    } catch { toast.error('Diagnosis failed'); }
    setLoading(false);
  };

  const verdictColor = diagnosis?.verdict === 'healthy' ? 'text-green-700 bg-green-50 border-green-200' :
    diagnosis?.verdict === 'critical' ? 'text-red-700 bg-red-50 border-red-200' : 'text-amber-700 bg-amber-50 border-amber-200';

  return (
    <div className="bg-white rounded-xl border border-border p-5 shadow-sm" data-testid="campaign-diagnosis-panel">
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold">Campaign Diagnosis</h3>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">Select a campaign to check tracking, landing page, traffic quality, and scan-start conversion.</p>

      <div className="flex items-end gap-2 mb-4">
        <div className="flex-1">
          {campaignNames.length > 0 ? (
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              className="w-full text-xs border border-border rounded-md px-3 py-2 bg-white"
              data-testid="diagnosis-campaign-select"
            >
              <option value="">Select campaign...</option>
              {campaignNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={selected}
              onChange={e => setSelected(e.target.value)}
              placeholder="Enter campaign name (utm_campaign value)"
              className="w-full text-xs border border-border rounded-md px-3 py-2"
              data-testid="diagnosis-campaign-input"
            />
          )}
        </div>
        <button
          onClick={() => runDiagnosis(selected)}
          disabled={!selected || loading}
          className="px-4 py-2 text-xs bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
          data-testid="run-diagnosis-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          Diagnose
        </button>
      </div>

      {diagnosis && (
        <div className="space-y-2" data-testid="diagnosis-results">
          <div className={`rounded-lg border p-3 ${verdictColor}`}>
            <p className="text-xs font-semibold">{diagnosis.verdict_text}</p>
            {diagnosis.priority_action && <p className="text-[11px] mt-1 opacity-80">{diagnosis.priority_action}</p>}
          </div>
          {diagnosis.checks.map((check, i) => {
            const st = CHECK_STYLE[check.status] || CHECK_STYLE.warn;
            const Icon = CHECK_ICON[check.status] || AlertTriangle;
            return (
              <div key={i} className={`rounded-lg border p-2.5 ${st.bg}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${st.cls}`} />
                  <span className="text-[11px] font-semibold">{check.name}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed ml-6">{check.detail}</p>
                {check.action && (
                  <div className="flex items-start gap-1 mt-1.5 ml-6">
                    <ArrowRight className="w-3 h-3 flex-shrink-0 mt-0.5 text-primary" />
                    <p className="text-[10px] text-primary font-medium">{check.action}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdsTab({ token }) {
  const [imports, setImports] = useState([]);
  const [attribution, setAttribution] = useState([]);
  const [selectedImport, setSelectedImport] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);

  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const loadData = async () => {
    try {
      const [importsRes, attrRes] = await Promise.all([
        axios.get(`${API}/admin/ads/imports`, opts),
        axios.get(`${API}/admin/ads/attribution`, opts),
      ]);
      setImports(importsRes.data.imports || []);
      setAttribution(attrRes.data.attribution || []);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { if (token) loadData(); }, [token]);

  const handleImported = async (importId) => {
    await loadData();
    const imp = (await axios.get(`${API}/admin/ads/imports`, opts)).data.imports?.find(i => i.import_id === importId);
    if (imp) setSelectedImport(imp);
  };

  const handleAnalyze = async (importId) => {
    setAnalyzing(true);
    try {
      await axios.post(`${API}/admin/ads/analyze/${importId}`, {}, opts);
      toast.success('Analysis started — refreshing in 15s...');
      setTimeout(async () => {
        await loadData();
        const updated = (await axios.get(`${API}/admin/ads/imports`, opts)).data.imports?.find(i => i.import_id === importId);
        if (updated) setSelectedImport(updated);
        setAnalyzing(false);
      }, 15000);
    } catch { toast.error('Analysis failed'); setAnalyzing(false); }
  };

  if (loading) return <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="ads-tab">
      {/* Campaign Launch Bot — Phase 1-4 */}
      <CampaignLaunchPanel token={token} />

      {/* Campaign Diagnosis — always visible */}
      <CampaignDiagnosisPanel campaigns={imports} token={token} />

      <ImportForm token={token} onImported={handleImported} />

      {/* Attribution Dashboard */}
      <div className="bg-white rounded-xl border border-border p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Campaign → Powersure Funnel Attribution</h3>
        </div>
        <AttributionTable data={attribution} />
      </div>

      {/* Import history + analysis */}
      {imports.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-5 shadow-sm">
          <h3 className="text-sm font-semibold mb-3">Imported Data</h3>
          <div className="space-y-2">
            {imports.map(imp => {
              const isSelected = selectedImport?.import_id === imp.import_id;
              const hasAnalysis = !!imp.analysis;
              return (
                <div key={imp.import_id} className={`border rounded-lg overflow-hidden ${isSelected ? 'border-primary' : 'border-border'}`}>
                  <div className="flex items-center justify-between px-3 py-2 cursor-pointer" onClick={() => setSelectedImport(isSelected ? null : imp)}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium">{imp.campaign_name}</span>
                      <span className="text-muted-foreground">{imp.date_range}</span>
                      <span className="text-muted-foreground">€{imp.performance?.cost || 0}</span>
                      {hasAnalysis && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Analyzed</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {!hasAnalysis && (
                        <button onClick={(e) => { e.stopPropagation(); handleAnalyze(imp.import_id); }}
                          disabled={analyzing}
                          className="px-2 py-1 text-[10px] bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`analyze-${imp.import_id}`}>
                          {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                          Analyze
                        </button>
                      )}
                    </div>
                  </div>
                  {isSelected && <div className="px-3 pb-3"><AnalysisView analysis={imp.analysis} /></div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
